import React, { useEffect, useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toggleSideBar } from "../../redux/header/headerAction";
import { filterNavigationItems } from "../../common/permissionController";
import DashboardIcon from "../../assets/images/dashboard-icon.svg";
import UsersIcon from "../../assets/images/users.svg";
import organisationIcon from "../../assets/images/organization-chart.svg";
import salarystructure from "../../assets/images/salarystructure.svg";
import attenance from "../../assets/images/attendance.png";
import calendar from "../../assets/images/calendar.svg";
import kpi from "../../assets/images/indicator.svg";
import assets from "../../assets/images/briefcase-2-line.svg";
import leave from "../../assets/images/calendar-event-line.svg";
import announcements from "../../assets/images/announcement.svg";
import { ReadOutlined, ApartmentOutlined, ScheduleOutlined, CloseOutlined, SolutionOutlined } from "@ant-design/icons";
import { Modal } from "antd";
import moment from 'moment';
import { RosterApiResponse, UserIdState } from "../../pages/Roster/types";
import { OrganisationIdState } from "../../pages/announcement/layouts/mailList/mailListsInterface";

import "./Sidebar.scss";
import { useGetApiRequests } from "../../common/CommonExport";
import { RootState, NavigationItem, RolePermission } from "./types";

export default function Sidebar() {
    const location = useLocation();
    const navigate = useNavigate();
    const rolePermissions = useSelector((state: RootState) => state?.rolePermissions?.rolePermissions);
    const roleName = useSelector((state: RootState) => state?.loginUserData?.userData?.data?.role?.name);
    const allowedRoles = ['IT Admin', 'Reporting Manager'];
    const showOrgSidebar = allowedRoles.includes(roleName);
    const isReportingManger = roleName === 'Reporting Manager';
    const isHRAdmin = roleName === 'HR Admin';
    const showKpi = rolePermissions?.some(
        (permission: RolePermission) => permission?.component?.name === "KPI" && permission?.isView
    );


    const employeePermission = useSelector((state: RootState) => state?.employeePermissionSlice?.employeePermission);
    const isSidebarOpen = useSelector((state: RootState) => state.toggleSideBar.data);
    const [isToggle, setIsToggle] = useState<boolean>();
    const dispatch = useDispatch();
    const [sideBarList, setSideBarList] = useState<NavigationItem[]>([]);
    const [rosterData, setRosterData] = useState<RosterApiResponse['data'] | null>(null);

    const orgId = useSelector((state: OrganisationIdState) => state?.organisationId?.id);
    const reportingManagerId = useSelector((state: UserIdState) => state?.userId?.id);
    const getRosterPlan = useGetApiRequests('getRosterPlan', 'GET');
    const memoizedGetRosterPlan = useMemo(() => getRosterPlan, []);

    const navigationItems: NavigationItem[] = [
        { name: "Dashboard", path: "dashboard", permission: "Dashboard", icon: DashboardIcon },
        { name: "Users", path: "users", permission: "User", icon: UsersIcon },
        { name: "Organization", path: "organisation", permission: "Organisation", icon: organisationIcon },
        ...(showKpi ? [{ name: "KPI", path: "dashboard/kpi", permission: "KPI", icon: kpi }] : []),
        { name: "Asset Master", path: "asset-master", icon: assets },
        { name: "Salary Structure", path: "salary-master", permission: "Salary Structure", icon: salarystructure },
        ...(!isHRAdmin ? [{ name: "Leave Management", path: "dashboard/leaveMangement", permission: "", icon: leave }] : []),
        ...(!isHRAdmin ? [{ name: "On Duty", path: "dashboard/on-duty", permission: "", icon: SolutionOutlined }] : []),
        ...(isHRAdmin ? [{ name: "Leave & On Duty Management", path: "dashboard/leave-onduty-Management", permission: "", icon: SolutionOutlined }] : []),
        // { name: 'Learning management', path: 'dashboard/learning-management', permission: '', icon: graduation },
        { name: "Holiday", path: "dashboard/holiday", permission: "", icon: calendar },
        { name: "Attendance", path: "dashboard/attanance", permission: "", icon: attenance },

        { name: "Announcement", path: "announcements/all", permission: "", icon: announcements },
        ...(showOrgSidebar ? [{
            name: "Organization Chart",
            path: "organization-chart",
            permission: "Organisation Chart",
            icon: ApartmentOutlined,
        }] : []),
        ...(isReportingManger && rosterData?.status?.message !== "Roster Plan not eligible for this department" ? [{
            name: "Roster Plan",
            path: "roster",
            permission: "",
            icon: ScheduleOutlined,
        }] : [])
    ];

    const employeeNavigationItems: NavigationItem[] = [
        { name: "User Dashboard", path: "user-dashboard", permission: "User Dashboard", icon: DashboardIcon },
        {
            name: "Employee Handbook",
            path: "employee-handbook",
            permission: "User Dashboard",
            icon: ReadOutlined,
        },
        {
            name: "Organization Chart",
            path: "organization-chart",
            permission: "Organisation Chart",
            icon: ApartmentOutlined,
        }
    ];
    const currentUrl = window.location.href;
    const urlParts = currentUrl.split("/");
    const currentUrlName = urlParts[urlParts.length - 1];

    useEffect(() => {
        let filteredNavigationItems = filterNavigationItems(navigationItems, rolePermissions);
        let filteredItemsExcludingAssetMaster = filteredNavigationItems.filter((item: NavigationItem) => item.name !== "Asset Master");
        const finalNavigationItems = employeePermission ? employeeNavigationItems : filteredItemsExcludingAssetMaster;
        setSideBarList(finalNavigationItems);
        setIsToggle(isSidebarOpen);
    }, [isSidebarOpen, employeePermission, rolePermissions, rosterData]);

    useEffect(() => {
        const fetchRosterData = async () => {
            if (isReportingManger && orgId && reportingManagerId) {
                try {
                    const response = await memoizedGetRosterPlan(null, {
                        organisationId: orgId,
                        reportingMangerId: reportingManagerId,
                        start: 0,
                        limit: 10
                    }, {
                        monthYear: moment().format('MM-YYYY')
                    }) as RosterApiResponse;

                    if (response?.data) {
                        setRosterData(response.data);

                    }
                } catch (error) {
                    console.error("Failed to fetch roster plan in sidebar:", error);
                }
            }
        };

        fetchRosterData();
    }, [isReportingManger, orgId, reportingManagerId, memoizedGetRosterPlan]);

    const routesToHideHeader = ["/", "/login", "/forgot-password", "/set-password", "/reset-password", "/home"];
    if (routesToHideHeader.includes(location.pathname)) {
        return null;
    }
    const handleNavigate = (item: NavigationItem) => {
        const path = item.path;
        if (path === "dashboard") {
            navigate(`/home/dashboard`);
        } else if (path === "user-dashboard") {
            navigate(`/user-dashboard`);
        } else if (path === "employee-handbook") {
            if (Number(orgId) === 562) {
                navigate('/employee-handbook/cnw');
            } else {
                navigate('/employee-handbook/krissco');
            }
        } else if (path === "user-dashboard/user-holiday") {
            navigate(`/user-dashboard/user-holiday`);
        } else if (path === "user-dashboard/user-attendance") {
            navigate(`/user-dashboard/user-attendance`);
        } else if (path === "organization-chart") {
            navigate(`/organization-chart`)
        } else {
            navigate(`/home/${path}`);
        }
        dispatch(toggleSideBar(false));
    };

    return (
        <>
            <div className="sidebar-container">
                {sideBarList?.length > 0 && (
                    <div className={isToggle ? "sidebar-container__toggle" : " sidebar-container__toggle__active"}>
                        <ul className="sidebar-container__menu">
                            {sideBarList.map((item: NavigationItem, index: number) => (
                                <li className={`${currentUrlName === item?.path ? "active-li" : ""}`} key={`${item?.name}_${index + 1}`} onClick={() => handleNavigate(item)}>
                                    <div className="flex items-center sidebar-icon">
                                        <div className="icon">
                                            {item.name === "Employee Handbook" ? (
                                                <ReadOutlined style={{ fontSize: 18 }} />
                                            ) : item.name === "Organization Chart" ? (
                                                <ApartmentOutlined style={{ fontSize: 18 }} />
                                            ) : item.name === "Roster Plan" ? (
                                                <ScheduleOutlined style={{ fontSize: 18 }} />
                                            ) : item.name === "On Duty" || item.name === "Leave & On Duty Management" ? (
                                                <SolutionOutlined style={{ fontSize: 18 }} />
                                            ) : (
                                                <img src={item?.icon as string} alt={item?.name} style={{ width: '18px' }} />
                                            )}
                                        </div>
                                        <span className="text-[12px] 2xl:text-[16px]">{item.name}</span>
                                    </div>

                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </>
    );
}
